export const PROBLEM_CONTENT_TYPE = 'application/problem+json';
/** The server answered with a problem document. `problem` is the contract's shape, in full. */
export class ApiProblemError extends Error {
    name = 'ApiProblemError';
    problem;
    /** The HTTP status. Also on `problem.status`; here because that is where a caller looks. */
    status;
    /** The catalogue code: `session_already_ended`, `validation_failed` (spec/08 §2.6). */
    code;
    /** `X-Request-Id`, the same id as the trace. Quote it in a support conversation. */
    requestId;
    /** Field-level validation problems, when the failure was a `400`. */
    fieldErrors;
    constructor(problem, status) {
        super(problem.detail ?? problem.title);
        this.problem = problem;
        this.status = status;
        this.code = problem.code;
        this.requestId = problem.requestId;
        this.fieldErrors = problem.errors ?? [];
    }
}
/**
 * A refusal from better-auth's endpoints under `/v1/auth/*`, which answer `{ code, message }` rather
 * than a problem document — the contract's `AuthError` (spec/08 §4.1). `code` is stable and upper
 * snake case (`INVALID_EMAIL_OR_PASSWORD`, `RATE_LIMITED`); `body` keeps whatever else the refusal
 * carried, such as the `suggestions` a taken gamer tag comes back with.
 */
export class ApiAuthError extends Error {
    name = 'ApiAuthError';
    /** The HTTP status. */
    status;
    /** better-auth's code, or ours in the same shape. */
    code;
    /** The whole refusal, as sent. */
    body;
    /** `X-Request-Id`, the same id as the trace. */
    requestId;
    constructor(body, status, requestId) {
        super(body.message);
        this.body = body;
        this.status = status;
        this.code = body.code;
        if (requestId !== undefined)
            this.requestId = requestId;
    }
}
/** The request never produced a problem document: a network failure, or a reply from a proxy. */
export class ApiTransportError extends Error {
    name = 'ApiTransportError';
    /** The HTTP status, when there was a response at all. */
    status;
    requestId;
    constructor(message, details = {}) {
        super(message, details.cause === undefined ? undefined : { cause: details.cause });
        if (details.status !== undefined)
            this.status = details.status;
        if (details.requestId !== undefined)
            this.requestId = details.requestId;
    }
}
/** A programming error in the caller — an unknown query parameter, a missing path parameter. */
export class ApiUsageError extends Error {
    name = 'ApiUsageError';
}
function isProblem(value) {
    if (typeof value !== 'object' || value === null)
        return false;
    const candidate = value;
    return (typeof candidate.type === 'string' &&
        typeof candidate.title === 'string' &&
        typeof candidate.status === 'number' &&
        typeof candidate.code === 'string' &&
        typeof candidate.requestId === 'string');
}
function isAuthError(value) {
    if (typeof value !== 'object' || value === null || Array.isArray(value))
        return false;
    const candidate = value;
    return typeof candidate.code === 'string' && candidate.code !== '' && typeof candidate.message === 'string';
}
/**
 * Turn a failed response into the right error.
 *
 * A body that claims to be problem+json but is not shaped like one is treated as a transport
 * failure, not coerced into a half-empty `Problem`: a `code` of `undefined` reaching a `switch` in
 * a console is how a user ends up staring at "something went wrong".
 */
export async function toApiError(response, requestId) {
    const contentType = response.headers.get('content-type') ?? '';
    const details = { status: response.status, ...(requestId === undefined ? {} : { requestId }) };
    let text;
    try {
        text = await response.text();
    }
    catch (cause) {
        return new ApiTransportError(`HTTP ${response.status}: the response body could not be read.`, {
            ...details,
            cause,
        });
    }
    if (contentType.includes('json')) {
        let body;
        try {
            body = JSON.parse(text);
        }
        catch (cause) {
            return new ApiTransportError(`HTTP ${response.status}: the response body is not valid JSON.`, {
                ...details,
                cause,
            });
        }
        if (isProblem(body))
            return new ApiProblemError(body, response.status);
        // Checked second, so a problem document is never mistaken for one: it carries `code` too.
        if (isAuthError(body))
            return new ApiAuthError(body, response.status, requestId);
    }
    const snippet = text.slice(0, 200).replace(/\s+/g, ' ').trim();
    return new ApiTransportError(`HTTP ${response.status}: expected ${PROBLEM_CONTENT_TYPE} but got "${contentType || 'no content type'}"` +
        `${snippet === '' ? '' : ` — ${snippet}`}`, details);
}
//# sourceMappingURL=problem.js.map